import React from 'react';
import { motion } from 'framer-motion';
import { FiArrowRight, FiPlay } from 'react-icons/fi';
import { productData } from '../data/constants';

const Hero = () => {
  return (
    <section className="min-h-screen flex items-center justify-center relative overflow-hidden pt-20">
      {/* Background Effects */}
      <div className="absolute inset-0 z-0">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 w-96 h-96 bg-cyan-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse delay-500"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-100 mb-6">
              <span className="w-2 h-2 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full mr-2"></span>
              <span className="text-sm font-medium text-blue-700">
                AI-Powered Platform
              </span>
            </div>

            <h1 className="text-5xl md:text-7xl font-bold leading-tight mb-6">
              <span className="block">Digital Apps.</span>
              <span className="gradient-text">Smarter with AI.</span>
            </h1>

            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              {productData.description}
            </p>

            <div className="flex flex-wrap gap-4 mb-8">
              {['Contacts', 'Events', 'Automation', 'AI'].map((tag, index) => (
                <motion.span
                  key={tag}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.1 }}
                  className="px-4 py-2 bg-white rounded-lg shadow-sm border border-gray-100 text-gray-700 font-medium hover:shadow-md transition-shadow"
                >
                  {tag}
                </motion.span>
              ))}
            </div>

            <div className="flex flex-wrap gap-4">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="gradient-btn flex items-center space-x-2"
              >
                <span>Explore the Platform</span>
                <FiArrowRight className="w-5 h-5" />
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="flex items-center space-x-2 px-6 py-3 bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all"
              >
                <div className="w-10 h-10 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 flex items-center justify-center">
                  <FiPlay className="w-5 h-5 text-white" />
                </div>
                <div className="text-left">
                  <div className="text-sm text-gray-500">Watch Demo</div>
                  <div className="font-semibold">2:30 min</div>
                </div>
              </motion.button>
            </div>
          </motion.div>

          {/* Right Content - 3D Mockup */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            <div className="relative w-full max-w-2xl mx-auto">
              {/* Main Mockup */}
              <div className="relative z-10 w-full aspect-[4/3] rounded-3xl shadow-2xl overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-gray-900 to-gray-800">
                  {/* Mockup Content */}
                  <div className="p-8 h-full flex flex-col">
                    <div className="flex items-center justify-between mb-8">
                      <div className="flex items-center space-x-3">
                        <div className="w-3 h-3 rounded-full bg-red-400"></div>
                        <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                        <div className="w-3 h-3 rounded-full bg-green-400"></div>
                      </div>
                      <div className="text-white text-sm font-medium">
                        XDigitalApps Dashboard
                      </div>
                      <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500"></div>
                    </div>

                    <div className="grid grid-cols-3 gap-4 flex-grow">
                      {/* Contact Cards */}
                      <div className="col-span-2 space-y-4">
                        <div className="bg-gray-800 rounded-xl p-4 animate-pulse">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500"></div>
                            <div>
                              <div className="w-32 h-4 bg-gray-700 rounded mb-2"></div>
                              <div className="w-24 h-3 bg-gray-700 rounded"></div>
                            </div>
                          </div>
                        </div>
                        <div className="bg-gray-800 rounded-xl p-4 animate-pulse delay-300">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-r from-purple-500 to-pink-500"></div>
                            <div>
                              <div className="w-28 h-4 bg-gray-700 rounded mb-2"></div>
                              <div className="w-20 h-3 bg-gray-700 rounded"></div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* AI Insights */}
                      <div className="bg-gradient-to-br from-blue-900/50 to-cyan-900/50 rounded-xl p-4">
                        <div className="text-blue-300 text-sm mb-2">AI Insights</div>
                        <div className="space-y-2">
                          <div className="w-full h-2 bg-blue-700/50 rounded-full overflow-hidden">
                            <div className="w-3/4 h-full bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full"></div>
                          </div>
                          <div className="w-full h-2 bg-blue-700/50 rounded-full overflow-hidden">
                            <div className="w-1/2 h-full bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full"></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating Elements */}
              <motion.div
                animate={{ y: [0, -20, 0] }}
                transition={{ repeat: Infinity, duration: 3 }}
                className="absolute -top-6 -right-6 w-32 h-32 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 shadow-2xl flex items-center justify-center"
              >
                <span className="text-white text-4xl">📅</span>
              </motion.div>

              <motion.div
                animate={{ y: [0, 20, 0] }}
                transition={{ repeat: Infinity, duration: 3, delay: 0.5 }}
                className="absolute -bottom-6 -left-6 w-24 h-24 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-500 shadow-2xl flex items-center justify-center"
              >
                <span className="text-white text-3xl">🤖</span>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;